import React from "react";
import { isChunkLoadError, reloadForNewBuild } from "./chunkReload";

// Drop-in replacement for React.lazy that survives a deploy landing mid-session.
//
// React.lazy caches the rejected promise: once a lazy component's import fails,
// React re-throws the SAME error on every subsequent render of that route. So
// without this wrapper, a single 404'd chunk poisons that route for the rest of
// the session and the boundary's "Try again" button can never clear it.
//
// Two failure modes, two different responses:
//   - a transient network blip  -> retrying the import fixes it, no reload
//   - the build was replaced    -> the file is gone; only a reload fixes it
// We cannot tell them apart from the error, so try the cheap one first (one
// extra request) and fall back to the reload.
export const lazyWithRetry = (importer) =>
  React.lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      try {
        return await importer();
      } catch (retryError) {
        if (reloadForNewBuild()) {
          // The reload is queued but this tick keeps running. Resolving or
          // rejecting now would flash the crash card for the frame before the
          // navigation happens; a promise that never settles keeps the Suspense
          // fallback on screen until the page goes away.
          return new Promise(() => {});
        }
        // Already reloaded recently and the chunk is still missing: this is a
        // broken deploy, not a superseded one. Let the boundary show it.
        throw retryError;
      }
    }
  });

export default lazyWithRetry;
